import { Component, signal, computed, inject, linkedSignal, resource, effect, ChangeDetectionStrategy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Customer } from '../../customer/customer.model';
import { Company, CreateSynchronizationContract } from '../synchronization.model';
import { SynchronizationService } from '../synchronization.service';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { CustomerService } from '../../customer/customer.service';
import { DepartmentService } from 'src/app/services/department.service';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-synchronization-create',
  templateUrl: './synchronization-create.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FormsModule]
})
export class SynchronizationCreateComponent {
  // Services
  public readonly activeModal = inject(NgbActiveModal);
  private readonly customerService = inject(CustomerService);
  private readonly synchronizationService = inject(SynchronizationService);
  private readonly toastrService = inject(ToastrService);
  private readonly departmentService = inject(DepartmentService);
  private readonly router = inject(Router);

  readonly modalContext = signal<{ customerId: number | null, companyId: number | null }>({ customerId: null, companyId: null });

  readonly customersResource = resource({
    params: () => ({ selectedDepartment: this.departmentService.selectedDepartment() }),
    loader: async ({ params }): Promise<Customer[]> => {
      if (!params.selectedDepartment) {
        return [];
      }
      return this.customerService.getCustomerList(params.selectedDepartment.id);
    }
  });

  readonly customers = computed(() => this.customersResource.value() ?? []);
  readonly selectedCustomerId = linkedSignal({
    source: this.customers,
    computation: (customers) => {
      const context = this.modalContext();
      if (context != null) {
        return context.customerId;
      }
      return customers.length > 0 ? customers[0].customerId : null;
    }
  });

  readonly companiesResource = resource({
    params: () => ({ customerId: this.selectedCustomerId() }),
    loader: async ({ params }): Promise<Company[]> => {
      if (!params.customerId) {
        return [];
      }
      return this.synchronizationService.getCompanies(params.customerId);
    }
  });

  readonly companies = computed(() => this.companiesResource.value() ?? []);
  readonly selectedCompanyId = linkedSignal({
    source: this.companies,
    computation: (companies) => {
      const context = this.modalContext();
      if (context != null && context.companyId != null) {
        return context.companyId;
      }
      return companies.length > 0 ? companies[0].companyId : null;
    }
  });

  readonly name = signal('');
  readonly saving = signal(false);

  readonly customerIdWasPreset = computed(() => {
    const context = this.modalContext();
    return context.customerId !== null;
  });

  readonly lockCompanySelection = computed(() => {
    const contextCompanyidSet = this.modalContext()?.companyId !== null;
    const customerIdNotSet = this.selectedCustomerId() === null;
    const companiesNotLoaded = this.companiesResource.isLoading();
    const companiesEmpty = this.companies().length === 0;
    return contextCompanyidSet || customerIdNotSet || companiesNotLoaded || companiesEmpty;
  });

  readonly selectedDepartment = computed(() => {
    return this.departmentService.getDepartment();
  });

  readonly customer = computed(() => {
    const customers = this.customers();
    const selectedId = this.selectedCustomerId();
    return customers.find(c => c.customerId === selectedId) ?? {} as Customer;
  });

  readonly canSave = computed(() => {
    const companyId = this.selectedCompanyId();
    const validCustomer = this.selectedCustomerId() !== null;
    const validCompany = companyId !== null;
    const companies = this.companies();
    const companyExists = Array.isArray(companies) && companies.some(c => c.companyId == companyId);

    return validCustomer && validCompany && companyExists && !this.saving();
  });

  async createSync() {
    this.saving.set(true);
    const customerId = this.selectedCustomerId();
    const companyId = this.selectedCompanyId();
    const departmentId = this.departmentService.selectedDepartment()?.id;

    if (!customerId || !companyId || !departmentId) {
      return;
    }

    const data: CreateSynchronizationContract = {
      customerId: customerId,
      companyId: companyId,
      name: this.name(),
      departmentId: departmentId
    };

    try {
      const newid = await this.synchronizationService.createSynchronization(data);
      this.toastrService.success("Synkroniseringskontrakt er oprettet");
      this.router.navigate(['/synchronization/' + newid]);
      this.activeModal.close("close click");
    } catch {
      // HTTP-fejl vises globalt af GlobalErrorInterceptor/ErrorService
    } finally {
      this.saving.set(false);
    }
  }

} 
